import { LANGUAGES, LANGUAGES_MAP, STORAGE_KEY } from "@constants/misc";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { Language, LanguageItem } from "@types";
import { getLocaleFlagUrl } from "@utils/locale-flags";
import { getValidLanguage } from "@utils/locales";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useLanguages = (): {
  languages: LanguageItem[];
  currentLanguage: LanguageItem;
  changeLanguage: (language: Language) => void;
  isMultiLanguage: boolean;
} => {
  const { locales = [], countryCode } = useMemberSettings();
  const { i18n } = useTranslation();

  const changeLanguage = useCallback(
    (language: Language) => {
      localStorage.setItem(STORAGE_KEY.LANGUAGE_MANUAL, "true");
      return i18n.changeLanguage(language);
    },
    [i18n],
  );

  const languages = useMemo(() => {
    return locales?.length
      ? locales?.map((locale) => {
          return {
            ...locale,
            // TODO: Use locale.displayName later when backend supports it
            displayName: LANGUAGES_MAP[locale.code as Language]?.displayName,
          };
        })
      : LANGUAGES;
  }, [locales]);

  const currentLanguage = useMemo(() => {
    const currentLangCode = getValidLanguage(languages, i18n.language as Language, LANGUAGES[0].code as Language);
    return languages.find((l) => l.code === currentLangCode) ?? LANGUAGES[0];
  }, [i18n.language, languages]);

  const languageItems = useMemo(() => {
    return languages.map((lang) => {
      // Priority: backend override > getLocaleFlagUrl (handles country-aware + default flags)
      const flagURL = lang.flagImage?.url || getLocaleFlagUrl(countryCode, lang.code) || "";

      return {
        code: lang.code as Language,
        name: lang.displayName,
        flagURL,
      };
    });
  }, [languages, countryCode]);

  const currentLanguageItem = useMemo(() => {
    // Priority: backend override > getLocaleFlagUrl (handles country-aware + default flags)
    const flagURL = currentLanguage.flagImage?.url || getLocaleFlagUrl(countryCode, currentLanguage.code) || "";

    return {
      code: currentLanguage.code as Language,
      name: currentLanguage.displayName,
      flagURL,
    };
  }, [currentLanguage, countryCode]);

  return {
    languages: languageItems,
    currentLanguage: currentLanguageItem,
    changeLanguage,
    isMultiLanguage: languages.length > 1,
  };
};
